import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withApollo } from '@apollo/client/react/hoc';

import { StaticContext, FiveHundredError } from '@sm/webassets';
import { Grid, Row, Col, ProgressCircle } from '@sm/wds-react';
import { t } from '@sm/intl';

import MRXPage from '../MRXPage';
import MRXHeader from './MRXHeader';
import ProductCategory from './ProductCategory';
import HereToHelp from './HereToHelp';
import UsedInOrgs from './UsedInOrgs';
import { modules as modulesQuery, user as userQuery } from './solutions.graphql';
import { PRODUCT_CATEGORY } from '../enums';
import filterProducts, { getFilterParamsFromUrlLocation } from './filter';
import { productCategoryPropType } from '../propTypes';
import getLogger, { LOGGER_NAME, getLinkGenerator } from '../helpers/logging';
import SynchronousQueryStream from '../helpers/SynchronousQueryStream';

import audienceImage from './audience-image.png';
import './mrx-solutions.scss';

export const COPY = {
  audiencePanel: {
    id: 'mrx.Solutions.audiencePanel',
    defaultMessage: 'Global survey panel',
    description: '[Type: Header][Vis: med] - Title of the Audience panel product solutions category',
  },
  expertSolutions: {
    id: 'mrx.Solutions.expertSolutions',
    defaultMessage: 'Expert solutions',
    description: '[Type: Header][Vis: med] - Title of the market research ready-made solutions category',
  },
  audiencePanelDescription: {
    id: 'mrx.Solutions.audiencePanelDescription',
    defaultMessage:
      'Run your own global market research in minutes. Design a custom survey in the SurveyMonkey platform, ' +
      'launch to your target audience, and receive real-time results.',
    description: '[Type: Paragraph][Vis: med] - Description of the Audience panel category of products',
  },
  expertSolutionsDescription: {
    id: 'mrx.Solutions.expertSolutionsDescription',
    defaultMessage:
      'Get powerful insights without doing a single calculation. Customize your study using our built-in expert methodology, ' +
      'launch to your target audience, and receive presentation-ready reports automatically.',
    description: '[Type: Paragraph][Vis: med] - Description of the market research ready-made category of products',
  },
  getStarted: {
    id: 'mrx.Solutions.getStarted',
    defaultMessage: 'Get started',
    description: '[Type: Link][Vis: med] - CTA for user to try out a market research product',
  },
  learnMore: {
    id: 'mrx.Solutions.learnMore',
    defaultMessage: 'Learn more',
    description: '[Type: Link][Vis: med] - CTA for user to learn more about a market research product',
  },
  contactUs: {
    id: 'mrx.Solutions.contactUs',
    defaultMessage: 'Contact us',
    description: '[Type: Link][Vis: med] - CTA for user to contact our support team about a market research product',
  },
  tryItNow: {
    id: 'mrx.Solutions.tryItNow',
    defaultMessage: 'Try it now',
    description: '[Type: Link][Vis: med] - CTA for user to contact our support team about a market research product',
  },
  surveyMonkeyAudience: {
    id: 'mrx.Solutions.surveyMonkeyAudience',
    defaultMessage: 'SurveyMonkey Audience',
    description: '[Type: Header][Vis: med] - Title of the Audience product',
  },
  surveyMonkeyAudienceDescription: {
    id: 'mrx.Solutions.surveyMonkeyAudienceDescription',
    defaultMessage:
      'Find people who fit your criteria from our global panel of respondents. Select region, age, gender, income, and more.',
    description: '[Type: Paragraph][Vis: med] - Description of the Audience product',
  },
};

const logger = getLogger(LOGGER_NAME.SOLUTIONS);
const biLogger = getLogger(LOGGER_NAME.BI_MARKETPLACE_MODAL);
const getLink = getLinkGenerator({
  ut_source: 'market_research',
  ut_source2: 'solutions',
  ut_source3: 'marketplace-cards',
});

const getAudienceCategory = ctaCopy => ({
  name: t(COPY.audiencePanel),
  description: t(COPY.audiencePanelDescription),
  priority: 1,
  products: [
    {
      name: t(COPY.surveyMonkeyAudience),
      description: t(COPY.surveyMonkeyAudienceDescription),
      image: audienceImage,
      ctaLink: {
        href: getLink('/collect/audience/preview', {
          ut_ctatext: ctaCopy.defaultMessage,
          ut_source3: 'sm-button',
        }),
        text: t(ctaCopy),
        logging: () => logger.click.getStarted('audience_panel'),
      },
      learnMoreLink: {
        href: getLink('/market-research/solutions/audience-panel', {
          ut_ctatext: 'Learn more',
        }),
        text: t(COPY.learnMore),
        logging: () => logger.click.learnMore('audience_panel'),
      },
    },
  ],
});

const MRX = ({ location, productCategories }) => {
  const [filterParams] = useState(getFilterParamsFromUrlLocation(location));
  const mProductCategories = filterProducts(productCategories, filterParams);
  const orderedCategories = Object.keys(mProductCategories);
  orderedCategories.sort((keyA, keyB) => {
    const priorityA = mProductCategories[keyA].priority;
    const priorityB = mProductCategories[keyB].priority;
    if (priorityA === undefined || priorityA === null) return 1;
    if (priorityB === undefined || priorityB === null) return -1;
    return priorityA - priorityB;
  });

  return (
    <Grid noBleeds noGutters fluid className="sm-mrx-solutions">
      <Row>
        <Col>
          <MRXHeader />
        </Col>
      </Row>
      {orderedCategories.map((pcKey, idx) => (
        <If condition={mProductCategories[pcKey].products.length}>
          <Row
            key={`mrx-product-category-${mProductCategories[pcKey].name}`}
            className="sm-mrx-solutions__product-category-row"
          >
            <Col>
              <ProductCategory
                title={mProductCategories[pcKey].name}
                description={mProductCategories[pcKey].description}
                products={mProductCategories[pcKey].products}
                first={idx === 0}
                anchor={pcKey}
              />
            </Col>
          </Row>
        </If>
      ))}
      <Row>
        <Col>
          <HereToHelp />
        </Col>
      </Row>
      <Row>
        <Col>
          <UsedInOrgs />
        </Col>
      </Row>
    </Grid>
  );
};

MRX.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
    hash: PropTypes.string,
  }),
  productCategories: PropTypes.objectOf(productCategoryPropType).isRequired,
};

MRX.defaultProps = {
  location: null,
};

const mapModules = (modules, ctaCopy) =>
  modules.map(module => {
    return {
      name: module.title,
      description: module.description,
      image: module.image,
      ctaLink: {
        href: getLink(`/solutions/create/${module.id}`, {
          ut_ctatext: ctaCopy.defaultMessage,
        }),
        text: t(ctaCopy),
        logging: () => {
          logger.click.getStarted(module.title);
          biLogger.click.getStarted(module.type, module.subtype);
        },
      },
      learnMoreLink: {
        href: getLink(module.links.marketingPage, {
          ut_ctatext: 'Learn more',
        }),
        text: t(COPY.learnMore),
        logging: () => {
          logger.click.learnMore(module.title);
          biLogger.click.learnMore();
        },
      },
    };
  });

const FETCH_STATE = Object.freeze({
  LOADING: 'LOADING',
  ERROR: 'ERROR',
  IDLE: 'IDLE',
});

const Wrapper = ({ location, client, useIntercom }) => {
  const {
    user: { isAuthenticated },
  } = useContext(StaticContext);
  const ctmProducts = useRef();
  const [fetchState, setFetchState] = useState(FETCH_STATE.LOADING);
  const ctaCopy = useRef(COPY.tryItNow);
  useEffect(() => biLogger.pageLoad(), []);
  useEffect(() => {
    new SynchronousQueryStream(client)
      .query({ query: modulesQuery })
      .resolveQueryResultToContext()
      .catch(err => {
        setFetchState(FETCH_STATE.ERROR);
        throw err;
      })
      .query({ query: userQuery })
      .resolveQueryResultToContext()
      // Don't block on this error; just move on since the user doesn't
      // have to be logged in
      .catch(() => null)
      .collect((_, context) => {
        const {
          modules: { items: modules },
          user,
        } = context;
        const hasPurchasedModule = user ? user.preferences.hasPurchasedModule : false;
        ctaCopy.current = isAuthenticated && hasPurchasedModule ? COPY.getStarted : COPY.tryItNow;
        ctmProducts.current = mapModules(modules, ctaCopy.current);
        setFetchState(FETCH_STATE.IDLE);
      });
  });
  let inner;
  if (fetchState === FETCH_STATE.LOADING) {
    inner = (
      <div className="sm-mrx-solutions__loader">
        <ProgressCircle size="lg" continuous />
      </div>
    );
  } else if (fetchState === FETCH_STATE.ERROR) {
    inner = <FiveHundredError />;
  } else {
    inner = (
      <MRX
        location={location}
        productCategories={{
          [PRODUCT_CATEGORY.AUDIENCE]: getAudienceCategory(ctaCopy.current),
          [PRODUCT_CATEGORY.EXPERT_SOLUTIONS]: {
            name: t(COPY.expertSolutions),
            description: t(COPY.expertSolutionsDescription),
            priority: 0,
            products: ctmProducts.current,
          },
        }}
      />
    );
  }
  return (
    <MRXPage
      pageTitle="home"
      useIntercom={useIntercom}
      canonicalLink="https://www.surveymonkey.com/market-research/solutions/"
    >
      {inner}
    </MRXPage>
  );
};

Wrapper.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
    hash: PropTypes.string,
  }),
  client: PropTypes.shape({
    query: PropTypes.func.isRequired,
  }).isRequired,
  useIntercom: PropTypes.bool,
};

Wrapper.defaultProps = {
  location: null,
  useIntercom: true,
};

export default withRouter(withApollo(Wrapper));
